/**
 * AdminCookbookPanel — куратор за публичната Готварска книга (iter595).
 *
 * Позволява на админ потребители да добавят, редактират и изтриват
 * curated рецепти за всички крайни потребители. Поддържа таксономия:
 *   - meal_type (закуска / обяд / вечеря / междинно)
 *   - dietary_patterns (мулти-избор: средиземноморска, DASH, MIND, …)
 *   - metabolic_pathways (мулти-избор: insulin-sensitive, anti-inflammatory, …)
 *
 * Backend: /api/public-recipes (GET) + /api/public-recipes/admin (POST/PUT/DELETE)
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Plus, Trash2, Pencil, Save, X as XIcon, Loader2, ChefHat, Search,
  Link2, Camera, Sparkles,
} from "lucide-react";

const MEAL_TYPES = [
  { id: "breakfast", label: "Закуска", icon: "🌅" },
  { id: "lunch",     label: "Обяд",    icon: "☀️" },
  { id: "dinner",    label: "Вечеря",  icon: "🌙" },
  { id: "snack",     label: "Междинно", icon: "🍎" },
];

const EMPTY_DRAFT = {
  name_bg: "",
  name_en: "",
  meal_type: "lunch",
  servings: 1,
  prep_min: 15,
  tags_bg: [],
  ingredients: [{ name_bg: "", name_en: "", amount_g: 100 }],
  instructions_bg: "",
  instructions_en: "",
  metabolic: { dil: null, nis: null, satiety: null, tier: null },
  dietary_patterns: [],
  metabolic_pathways: [],
  source_url: "",
};

export default function AdminCookbookPanel({ API, isDark }) {
  const [recipes, setRecipes] = useState([]);
  const [taxonomy, setTaxonomy] = useState({ dietary_patterns: [], metabolic_pathways: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterMeal, setFilterMeal] = useState("");
  const [filterDp, setFilterDp] = useState("");
  const [filterMp, setFilterMp] = useState("");
  const [editing, setEditing] = useState(null);  // recipe id or "new"
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);

  // iter596 — URL / Photo import
  const [importingUrl, setImportingUrl] = useState(false);
  const [importingPhoto, setImportingPhoto] = useState(false);
  const [urlDialogOpen, setUrlDialogOpen] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const photoInputRef = useRef(null);

  const fetchRecipes = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterMeal) params.set("meal_type", filterMeal);
      if (filterDp) params.set("dietary_pattern", filterDp);
      if (filterMp) params.set("metabolic_pathway", filterMp);
      const { data } = await axios.get(`${API}/public-recipes?${params.toString()}`);
      setRecipes(data?.recipes || []);
    } catch (e) {
      toast.error("Грешка при зареждане на рецептите");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    axios.get(`${API}/public-recipes/taxonomy`).then(({ data }) => {
      setTaxonomy({
        dietary_patterns: data?.dietary_patterns || [],
        metabolic_pathways: data?.metabolic_pathways || [],
      });
    }).catch(() => {});
  }, [API]);

  useEffect(() => {
    fetchRecipes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMeal, filterDp, filterMp]);

  const filtered = useMemo(() => {
    if (!search.trim()) return recipes;
    const s = search.trim().toLowerCase();
    return recipes.filter(r =>
      (r.name_bg || "").toLowerCase().includes(s) ||
      (r.name_en || "").toLowerCase().includes(s),
    );
  }, [recipes, search]);

  const beginCreate = () => {
    setDraft({ ...EMPTY_DRAFT, ingredients: [{ name_bg: "", name_en: "", amount_g: 100 }] });
    setEditing("new");
  };

  // iter596 — Hydrate the editor with the response from /recipe/url-import or
  // /recipe/vision-parse so the curator can review/edit before saving.
  const hydrateDraftFromExtraction = (extracted) => {
    const ingredients = (extracted?.ingredients || []).map((i) => ({
      name_bg: i.name_bg || i.name || "",
      name_en: i.name_en || "",
      amount_g: Number(i.amount_g) || Number(i.grams) || 100,
    }));
    setDraft({
      name_bg: extracted?.meal_name_bg || extracted?.name_bg || extracted?.name || "",
      name_en: extracted?.meal_name_en || extracted?.name_en || "",
      meal_type: extracted?.meal_type || "lunch",
      servings: extracted?.servings || 1,
      prep_min: extracted?.prep_min || 15,
      tags_bg: extracted?.tags_bg || [],
      ingredients: ingredients.length > 0 ? ingredients : [{ name_bg: "", name_en: "", amount_g: 100 }],
      instructions_bg: extracted?.instructions_bg || extracted?.instructions || "",
      instructions_en: extracted?.instructions_en || "",
      metabolic: extracted?.metabolic || { dil: null, nis: null, satiety: null, tier: null },
      dietary_patterns: extracted?.dietary_patterns || [],
      metabolic_pathways: extracted?.metabolic_pathways || [],
      source_url: extracted?.source_url || extracted?.url || "",
    });
    setEditing("new");
  };

  const importFromUrl = async () => {
    const url = (urlInput || "").trim();
    if (!url) { toast.error("Моля въведете URL"); return; }
    setImportingUrl(true);
    try {
      const { data } = await axios.post(`${API}/recipe/url-import`, {
        url, language: "bg",
      });
      hydrateDraftFromExtraction({ ...data, source_url: data.source_url || url });
      setUrlDialogOpen(false);
      setUrlInput("");
      toast.success("Рецептата е извлечена. Прегледай и запази.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка при импорт от URL");
    } finally {
      setImportingUrl(false);
    }
  };

  const importFromPhoto = async (file) => {
    if (!file) return;
    setImportingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      fd.append("language", "bg");
      const { data } = await axios.post(`${API}/recipe/vision-parse`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      hydrateDraftFromExtraction(data);
      toast.success("Снимката е разпозната. Прегледай и запази.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка при импорт от снимка");
    } finally {
      setImportingPhoto(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };

  const beginEdit = (recipe) => {
    setDraft({
      name_bg: recipe.name_bg || "",
      name_en: recipe.name_en || "",
      meal_type: recipe.meal_type || "lunch",
      servings: recipe.servings || 1,
      prep_min: recipe.prep_min || 15,
      tags_bg: recipe.tags_bg || [],
      ingredients: (recipe.ingredients || []).map(i => ({
        name_bg: i.name_bg || i.name || "",
        name_en: i.name_en || "",
        amount_g: Number(i.amount_g) || 100,
      })),
      instructions_bg: recipe.instructions_bg || "",
      instructions_en: recipe.instructions_en || "",
      metabolic: recipe.metabolic || { dil: null, nis: null, satiety: null, tier: null },
      dietary_patterns: recipe.dietary_patterns || [],
      metabolic_pathways: recipe.metabolic_pathways || [],
      source_url: recipe.source_url || "",
    });
    setEditing(recipe.id);
  };

  const cancel = () => {
    setEditing(null);
    setDraft(EMPTY_DRAFT);
  };

  const save = async () => {
    if (!draft.name_bg.trim()) { toast.error("Името е задължително"); return; }
    const ings = (draft.ingredients || []).filter(i => i.name_bg && i.name_bg.trim());
    if (ings.length === 0) { toast.error("Поне една съставка"); return; }
    const payload = {
      ...draft,
      ingredients: ings.map(i => ({
        name_bg: i.name_bg.trim(),
        name_en: (i.name_en || "").trim() || undefined,
        amount_g: Math.max(1, Math.min(2000, Math.round(Number(i.amount_g) || 100))),
      })),
      // Clean nulls in metabolic so backend doesn't reject
      metabolic: Object.fromEntries(
        Object.entries(draft.metabolic || {}).filter(([, v]) => v !== null && v !== "" && v !== undefined),
      ),
    };
    if (Object.keys(payload.metabolic).length === 0) delete payload.metabolic;
    setSaving(true);
    try {
      if (editing === "new") {
        await axios.post(`${API}/public-recipes/admin`, payload);
        toast.success("Рецептата е добавена ✓");
      } else {
        await axios.put(`${API}/public-recipes/admin/${editing}`, payload);
        toast.success("Рецептата е обновена ✓");
      }
      cancel();
      await fetchRecipes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка при запис");
    } finally {
      setSaving(false);
    }
  };

  const removeRecipe = async (id, name) => {
    if (!window.confirm(`Изтриване на „${name}" от публичната готварска книга?`)) return;
    try {
      await axios.delete(`${API}/public-recipes/admin/${id}`);
      toast.success("Изтрита");
      await fetchRecipes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка при изтриване");
    }
  };

  const toggleArrayItem = (field, value) => {
    setDraft((d) => {
      const arr = new Set(d[field] || []);
      if (arr.has(value)) arr.delete(value); else arr.add(value);
      return { ...d, [field]: Array.from(arr) };
    });
  };

  const updateIngredient = (idx, patch) => {
    setDraft((d) => {
      const arr = [...d.ingredients];
      arr[idx] = { ...arr[idx], ...patch };
      return { ...d, ingredients: arr };
    });
  };

  const addIngredient = () => {
    setDraft((d) => ({ ...d, ingredients: [...d.ingredients, { name_bg: "", name_en: "", amount_g: 100 }] }));
  };

  const removeIngredient = (idx) => {
    setDraft((d) => ({ ...d, ingredients: d.ingredients.filter((_, i) => i !== idx) }));
  };

  // ───────── render ─────────
  const cardCls = isDark
    ? "bg-gray-800/40 border-gray-700 text-gray-100"
    : "bg-white border-gray-200 text-gray-900";

  return (
    <div className="space-y-4" data-testid="admin-cookbook-panel">
      {/* Header */}
      <div className={`p-4 rounded-xl border ${cardCls}`}>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <ChefHat className="w-5 h-5 text-fuchsia-500" />
            <h2 className="text-lg font-bold">Готварска книга — куратор</h2>
            <span className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              {recipes.length} {recipes.length === 1 ? "рецепта" : "рецепти"}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {/* iter596 — URL import */}
            <Button
              variant="outline"
              onClick={() => setUrlDialogOpen(true)}
              disabled={importingUrl || importingPhoto}
              data-testid="admin-cookbook-import-url-btn"
            >
              {importingUrl ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Link2 className="w-4 h-4 mr-1" />}
              От URL
            </Button>
            {/* iter596 — Photo import */}
            <Button
              variant="outline"
              onClick={() => photoInputRef.current?.click()}
              disabled={importingUrl || importingPhoto}
              data-testid="admin-cookbook-import-photo-btn"
            >
              {importingPhoto ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Camera className="w-4 h-4 mr-1" />}
              От снимка
            </Button>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => importFromPhoto(e.target.files?.[0])}
              data-testid="admin-cookbook-photo-input"
            />
            <Button
              onClick={beginCreate}
              className="bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white"
              data-testid="admin-cookbook-add-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Нова
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mt-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Търсене..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9 text-sm"
              data-testid="admin-cookbook-search"
            />
          </div>
          <select
            value={filterMeal}
            onChange={(e) => setFilterMeal(e.target.value)}
            className={`h-9 text-sm rounded-md border px-2 ${isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-200"}`}
            data-testid="admin-cookbook-filter-meal"
          >
            <option value="">Всички хранения</option>
            {MEAL_TYPES.map(mt => <option key={mt.id} value={mt.id}>{mt.icon} {mt.label}</option>)}
          </select>
          <select
            value={filterDp}
            onChange={(e) => setFilterDp(e.target.value)}
            className={`h-9 text-sm rounded-md border px-2 ${isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-200"}`}
            data-testid="admin-cookbook-filter-dp"
          >
            <option value="">Всички модели</option>
            {taxonomy.dietary_patterns.map(dp => <option key={dp.id} value={dp.id}>{dp.label_bg}</option>)}
          </select>
          <select
            value={filterMp}
            onChange={(e) => setFilterMp(e.target.value)}
            className={`h-9 text-sm rounded-md border px-2 ${isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-200"}`}
            data-testid="admin-cookbook-filter-mp"
          >
            <option value="">Всички пътища</option>
            {taxonomy.metabolic_pathways.map(mp => <option key={mp.id} value={mp.id}>{mp.label_bg}</option>)}
          </select>
        </div>
      </div>

      {/* Editor */}
      {editing && (
        <div className={`p-4 rounded-xl border ${cardCls}`} data-testid="admin-cookbook-editor">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-base">
              {editing === "new" ? "Нова рецепта" : "Редакция"}
            </h3>
            <button onClick={cancel} className="text-gray-400 hover:text-gray-600" data-testid="admin-cookbook-editor-close">
              <XIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Basic info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase opacity-60">Име (BG) *</label>
              <Input value={draft.name_bg} onChange={(e) => setDraft({ ...draft, name_bg: e.target.value })} data-testid="admin-cookbook-name-bg" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase opacity-60">Name (EN)</label>
              <Input value={draft.name_en} onChange={(e) => setDraft({ ...draft, name_en: e.target.value })} data-testid="admin-cookbook-name-en" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase opacity-60">Хранене</label>
              <select
                value={draft.meal_type}
                onChange={(e) => setDraft({ ...draft, meal_type: e.target.value })}
                className={`h-9 w-full text-sm rounded-md border px-2 ${isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-200"}`}
                data-testid="admin-cookbook-meal-type"
              >
                {MEAL_TYPES.map(mt => <option key={mt.id} value={mt.id}>{mt.icon} {mt.label}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] font-bold uppercase opacity-60">Порции</label>
                <Input type="number" min="1" max="20" value={draft.servings} onChange={(e) => setDraft({ ...draft, servings: Number(e.target.value) })} data-testid="admin-cookbook-servings" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase opacity-60">Време (мин)</label>
                <Input type="number" min="0" max="600" value={draft.prep_min} onChange={(e) => setDraft({ ...draft, prep_min: Number(e.target.value) })} data-testid="admin-cookbook-prep-min" />
              </div>
            </div>
          </div>

          {/* Ingredients */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] font-bold uppercase opacity-60">Съставки *</label>
              <Button size="sm" variant="ghost" onClick={addIngredient} className="text-fuchsia-500" data-testid="admin-cookbook-add-ingredient">
                <Plus className="w-3 h-3 mr-1" /> Добави
              </Button>
            </div>
            <div className="space-y-1.5">
              {draft.ingredients.map((ing, i) => (
                <div key={i} className={`flex items-center gap-2 p-1.5 rounded ${isDark ? "bg-gray-700/30" : "bg-gray-50"}`}>
                  <Input
                    placeholder="Име (BG)"
                    value={ing.name_bg}
                    onChange={(e) => updateIngredient(i, { name_bg: e.target.value })}
                    className="flex-1 h-8 text-xs"
                    data-testid={`admin-cookbook-ingredient-name-bg-${i}`}
                  />
                  <Input
                    placeholder="Name (EN)"
                    value={ing.name_en || ""}
                    onChange={(e) => updateIngredient(i, { name_en: e.target.value })}
                    className="flex-1 h-8 text-xs"
                    data-testid={`admin-cookbook-ingredient-name-en-${i}`}
                  />
                  <Input
                    type="number"
                    min="1"
                    max="2000"
                    value={ing.amount_g}
                    onChange={(e) => updateIngredient(i, { amount_g: Number(e.target.value) })}
                    className="w-16 h-8 text-xs text-right"
                    data-testid={`admin-cookbook-ingredient-amount-${i}`}
                  />
                  <span className="text-[10px] text-gray-400">g</span>
                  <button
                    onClick={() => removeIngredient(i)}
                    className="w-7 h-7 rounded-full bg-red-100 hover:bg-red-200 text-red-600 text-xs"
                    data-testid={`admin-cookbook-ingredient-remove-${i}`}
                    aria-label="remove"
                  >✕</button>
                </div>
              ))}
            </div>
          </div>

          {/* Dietary patterns */}
          <div className="mt-4">
            <label className="text-[10px] font-bold uppercase opacity-60">Хранителни модели</label>
            <div className="flex flex-wrap gap-1 mt-1">
              {taxonomy.dietary_patterns.map(dp => (
                <button
                  key={dp.id}
                  onClick={() => toggleArrayItem("dietary_patterns", dp.id)}
                  data-testid={`admin-cookbook-dp-${dp.id}`}
                  className={`px-2 py-0.5 rounded-full text-[11px] transition-all ${
                    draft.dietary_patterns.includes(dp.id)
                      ? "bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white"
                      : isDark ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {dp.label_bg}
                </button>
              ))}
            </div>
          </div>

          {/* Metabolic pathways */}
          <div className="mt-3">
            <label className="text-[10px] font-bold uppercase opacity-60">Метаболитни пътища</label>
            <div className="flex flex-wrap gap-1 mt-1">
              {taxonomy.metabolic_pathways.map(mp => (
                <button
                  key={mp.id}
                  onClick={() => toggleArrayItem("metabolic_pathways", mp.id)}
                  data-testid={`admin-cookbook-mp-${mp.id}`}
                  className={`px-2 py-0.5 rounded-full text-[11px] transition-all ${
                    draft.metabolic_pathways.includes(mp.id)
                      ? "bg-gradient-to-r from-indigo-500 to-blue-500 text-white"
                      : isDark ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {mp.label_bg}
                </button>
              ))}
            </div>
            <p className={`text-[10px] mt-1 italic ${isDark ? "text-gray-500" : "text-gray-400"}`}>
              Оставете двете полета празни → системата ще предложи автоматично от съставките.
            </p>
          </div>

          {/* Metabolic scores (optional) */}
          <div className="mt-4">
            <label className="text-[10px] font-bold uppercase opacity-60 flex items-center gap-1">
              AI scores (по желание)
              <Sparkles className="w-3 h-3 text-fuchsia-500" />
              <span className="text-[9px] font-normal opacity-70">— оставете празни → системата ще ги изчисли при запис</span>
            </label>
            <div className="grid grid-cols-4 gap-2 mt-1">
              <Input type="number" placeholder="DIL" min="0" max="50" step="0.1"
                value={draft.metabolic.dil ?? ""}
                onChange={(e) => setDraft({ ...draft, metabolic: { ...draft.metabolic, dil: e.target.value === "" ? null : Number(e.target.value) } })}
                data-testid="admin-cookbook-metabolic-dil"
              />
              <Input type="number" placeholder="NIS %" min="0" max="100"
                value={draft.metabolic.nis ?? ""}
                onChange={(e) => setDraft({ ...draft, metabolic: { ...draft.metabolic, nis: e.target.value === "" ? null : Number(e.target.value) } })}
                data-testid="admin-cookbook-metabolic-nis"
              />
              <Input type="number" placeholder="Satiety %" min="0" max="100"
                value={draft.metabolic.satiety ?? ""}
                onChange={(e) => setDraft({ ...draft, metabolic: { ...draft.metabolic, satiety: e.target.value === "" ? null : Number(e.target.value) } })}
                data-testid="admin-cookbook-metabolic-satiety"
              />
              <select
                value={draft.metabolic.tier || ""}
                onChange={(e) => setDraft({ ...draft, metabolic: { ...draft.metabolic, tier: e.target.value || null } })}
                className={`h-9 text-sm rounded-md border px-2 ${isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-200"}`}
                data-testid="admin-cookbook-metabolic-tier"
              >
                <option value="">Tier</option>
                <option value="very_low">Много нисък</option>
                <option value="low">Нисък</option>
                <option value="moderate">Умерен</option>
                <option value="high">Висок</option>
              </select>
            </div>
          </div>

          {/* Instructions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            <div>
              <label className="text-[10px] font-bold uppercase opacity-60">Инструкции (BG)</label>
              <Textarea
                rows={3}
                value={draft.instructions_bg}
                onChange={(e) => setDraft({ ...draft, instructions_bg: e.target.value })}
                data-testid="admin-cookbook-instructions-bg"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase opacity-60">Instructions (EN)</label>
              <Textarea
                rows={3}
                value={draft.instructions_en}
                onChange={(e) => setDraft({ ...draft, instructions_en: e.target.value })}
                data-testid="admin-cookbook-instructions-en"
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase opacity-60 mt-3 block">URL източник (по желание)</label>
            <Input
              value={draft.source_url}
              onChange={(e) => setDraft({ ...draft, source_url: e.target.value })}
              placeholder="https://..."
              data-testid="admin-cookbook-source-url"
            />
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={cancel} disabled={saving}>
              Откажи
            </Button>
            <Button onClick={save} disabled={saving} className="bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white" data-testid="admin-cookbook-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Запази
            </Button>
          </div>
        </div>
      )}

      {/* Recipe list */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-fuchsia-500" />
        </div>
      ) : (
        <div className={`p-3 rounded-xl border ${cardCls}`}>
          <div className="space-y-1">
            {filtered.length === 0 ? (
              <p className={`text-center py-8 text-sm ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                Няма рецепти с тези филтри
              </p>
            ) : filtered.map((r) => (
              <div
                key={r.id}
                className={`flex items-start gap-3 p-2.5 rounded-lg ${isDark ? "hover:bg-gray-700/30" : "hover:bg-gray-50"}`}
                data-testid={`admin-cookbook-row-${r.id}`}
              >
                <span className="text-lg">{MEAL_TYPES.find(m => m.id === r.meal_type)?.icon || "🍴"}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{r.name_bg}</span>
                    {r.source_url && (
                      <a href={r.source_url} target="_blank" rel="noopener noreferrer"
                         className="text-[10px] text-blue-500 hover:underline">източник</a>
                    )}
                  </div>
                  <div className={`text-[11px] mt-0.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    {r.ingredients?.length || 0} съставки · {r.prep_min}мин
                    {r.metabolic?.dil != null && <> · DIL {r.metabolic.dil}</>}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(r.dietary_patterns || []).slice(0, 3).map((dp) => {
                      const meta = taxonomy.dietary_patterns.find(x => x.id === dp);
                      return (
                        <span key={dp} className="text-[9px] px-1.5 py-0.5 rounded-full bg-fuchsia-500/15 text-fuchsia-600 dark:text-fuchsia-300">
                          {meta?.label_bg || dp}
                        </span>
                      );
                    })}
                    {(r.metabolic_pathways || []).slice(0, 2).map((mp) => {
                      const meta = taxonomy.metabolic_pathways.find(x => x.id === mp);
                      return (
                        <span key={mp} className="text-[9px] px-1.5 py-0.5 rounded-full bg-indigo-500/15 text-indigo-600 dark:text-indigo-300">
                          {meta?.label_bg || mp}
                        </span>
                      );
                    })}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={() => beginEdit(r)} data-testid={`admin-cookbook-edit-${r.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => removeRecipe(r.id, r.name_bg)} className="text-red-500" data-testid={`admin-cookbook-delete-${r.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* iter596 — URL import dialog */}
      {urlDialogOpen && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-3 bg-black/60 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) setUrlDialogOpen(false); }}
          data-testid="admin-cookbook-url-dialog"
        >
          <div className={`w-full max-w-md rounded-2xl shadow-2xl border ${cardCls}`}>
            <div className={`px-4 py-3 border-b flex items-center justify-between ${isDark ? "border-white/10" : "border-gray-100"}`}>
              <div className="flex items-center gap-2">
                <Link2 className="w-4 h-4 text-fuchsia-500" />
                <h3 className="font-bold">Импортирай от URL</h3>
              </div>
              <button onClick={() => setUrlDialogOpen(false)} className="text-gray-400 hover:text-gray-600">
                <XIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-[10px] font-bold uppercase opacity-60">URL на рецептата</label>
                <Input
                  placeholder="https://www.allrecipes.com/recipe/..."
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !importingUrl) importFromUrl(); }}
                  disabled={importingUrl}
                  autoFocus
                  data-testid="admin-cookbook-url-input"
                />
                <p className={`text-[10px] mt-1 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                  Поддържат се сайтове с schema.org/Recipe (повечето кулинарни блогове).
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setUrlDialogOpen(false)} disabled={importingUrl}>
                  Откажи
                </Button>
                <Button
                  onClick={importFromUrl}
                  disabled={importingUrl || !urlInput.trim()}
                  className="bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white"
                  data-testid="admin-cookbook-url-import-confirm"
                >
                  {importingUrl ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                  Извлечи
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
